import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

type SingleSelectOption = {
  value: string;
  label: string;
  icon?: React.ElementType;
};

type Props = {
  name: string;
  options: SingleSelectOption[];
  placeholder: string;
  label?: string;
  className?: string;
  formItemClassName?: string;
  enableClear?: boolean;
  highlightWhenSelected?: boolean;
  placeholderIcon?: React.ReactNode;
};

export const SingleSelectInput = ({
  name,
  options,
  placeholder,
  label,
  className,
  formItemClassName,
  enableClear = true,
  highlightWhenSelected = true,
  placeholderIcon,
}: Props) => {
  const form = useFormContext();
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const handleClear = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          field.onChange("");
        };

        const currentOption = options.find(
          (option) => option.value === field.value,
        );

        const displayValue = currentOption ? (
          <div className="flex items-center gap-2">
            {currentOption.icon && <currentOption.icon className="h-4 w-4" />}
            <span>{currentOption.label}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground">
            {placeholderIcon}
            <span>{placeholder}</span>
          </div>
        );

        return (
          <FormItem className={formItemClassName}>
            {label && <FormLabel>{label}</FormLabel>}
            <Select
              value={field.value || ""}
              onValueChange={(newValue) => {
                field.onChange(newValue);
                setOpen(false);
                // Blur the trigger after selection
                setTimeout(() => {
                  (document.activeElement as HTMLElement)?.blur();
                }, 100);
              }}
              open={open}
              onOpenChange={setOpen}
            >
              <FormControl>
                <SelectTrigger
                  className={cn(
                    "w-fit gap-1 [&_svg.lucide-chevron-down]:hidden",
                    field.value &&
                      highlightWhenSelected &&
                      "bg-secondary text-primary",
                    className,
                  )}
                >
                  <div className="flex items-center gap-2 flex-1">
                    {displayValue}
                  </div>
                  {field.value && enableClear && (
                    <div
                      className="flex items-center"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClear(e);
                      }}
                    >
                      <X className="w-4 h-4 opacity-50 hover:opacity-100 cursor-pointer flex-shrink-0" />
                    </div>
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      {option.icon && <option.icon className="h-4 w-4" />}
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        );
      }}
    />
  );
};
